import { useEffect, type ReactNode } from 'react';
import type { FormTheme } from '@kushya/shared';

interface ThemeProviderProps {
  theme?: FormTheme;
  children: ReactNode;
}

interface HSL {
  h: number;
  s: number;
  l: number;
}

/**
 * Convert hex color to HSL format for CSS variables
 * Tailwind uses HSL format like: "hue saturation% lightness%"
 */
function hexToHSL(hex: string): HSL {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  // Parse RGB values
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Generate CSS variable overrides from theme object
 */
function generateCSSVariables(theme: FormTheme): string {
  const vars: string[] = [];

  if (theme.primaryColor) {
    const hsl = hexToHSL(theme.primaryColor);
    vars.push(`--primary: ${hsl.h} ${hsl.s}% ${hsl.l}%`);

    // Generate hover variant (slightly darker)
    vars.push(`--primary-hover: ${hsl.h} ${hsl.s}% ${Math.max(hsl.l - 10, 0)}%`);
  }

  if (theme.secondaryColor) {
    const hsl = hexToHSL(theme.secondaryColor);
    vars.push(`--secondary: ${hsl.h} ${hsl.s}% ${hsl.l}%`);
  }

  if (theme.accentColor) {
    const hsl = hexToHSL(theme.accentColor);
    vars.push(`--accent: ${hsl.h} ${hsl.s}% ${hsl.l}%`);
  }

  if (theme.backgroundColor) {
    const hsl = hexToHSL(theme.backgroundColor);
    vars.push(`--background: ${hsl.h} ${hsl.s}% ${hsl.l}%`);

    // Adjust foreground based on background lightness
    const foregroundL = hsl.l > 50 ? 10 : 90;
    vars.push(`--foreground: ${hsl.h} ${hsl.s}% ${foregroundL}%`);
  }

  if (theme.fontFamily) {
    vars.push(`--font-family: "${theme.fontFamily}", sans-serif`);
  }
  if (theme.headingFontFamily) {
    vars.push(`--heading-font-family: "${theme.headingFontFamily}", sans-serif`);
  }

  return vars.join('; ');
}

/**
 * Get Google Fonts URL for a given font family
 */
function getGoogleFontsUrl(fontFamily: string): string | null {
  // Skip system fonts that don't need Google Fonts
  const systemFonts = ['Inter', 'SF Pro', 'Roboto', 'Arial', 'Helvetica', 'system-ui'];
  if (systemFonts.includes(fontFamily)) return null;

  // Convert font name to Google Fonts format
  const fontName = fontFamily.replace(/ /g, '+');
  return `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
}

/**
 * Load Google Font dynamically
 */
function loadGoogleFont(fontFamily: string): () => void {
  const url = getGoogleFontsUrl(fontFamily);
  if (!url) return () => {};

  const linkId = `google-font-${fontFamily.replace(/ /g, '-')}`;

  // Check if already loaded
  if (document.getElementById(linkId)) {
    return () => {};
  }

  // Create and append link element
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);

  // Cleanup function
  return () => {
    const existingLink = document.getElementById(linkId);
    existingLink?.remove();
  };
}

export function ThemeProvider({ theme, children }: ThemeProviderProps) {
  useEffect(() => {
    if (!theme) return;

    const styleId = 'kushya-form-theme';
    let styleTag = document.getElementById(styleId) as HTMLStyleElement | null;

    // Create style tag if it doesn't exist
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    // Generate and inject CSS variables
    const cssVars = generateCSSVariables(theme);
    styleTag.textContent = `:root { ${cssVars} }`;

    // Apply font family to body
    if (theme.fontFamily) {
      document.body.style.fontFamily = `"${theme.fontFamily}", sans-serif`;
    }

    // Load custom fonts if specified
    const fontFamilies = [theme.fontFamily, theme.headingFontFamily].filter(
      (font): font is string => Boolean(font),
    );
    const uniqueFonts = Array.from(new Set(fontFamilies));
    const cleanups = uniqueFonts.map((font) => loadGoogleFont(font));

    // Cleanup function
    return () => {
      styleTag?.remove();
      cleanups.forEach((cleanup) => cleanup());
      if (theme.fontFamily) {
        document.body.style.fontFamily = '';
      }
    };
  }, [theme]);

  return <>{children}</>;
}
